import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, ShieldCheck, X } from "lucide-react";
import {
  getMarketingConsent,
  resetMarketingConsent,
  setMarketingConsent,
  subscribeToMarketingConsent,
} from "../lib/marketingAnalytics";

const MARKETING_ENABLED = process.env.REACT_APP_MARKETING_ANALYTICS_ENABLED === "true";

export default function MarketingConsentBanner() {
  const [visible, setVisible] = useState(
    () => MARKETING_ENABLED && getMarketingConsent() === null,
  );

  useEffect(() => {
    if (!MARKETING_ENABLED) return undefined;

    const syncVisibility = ({ detail } = {}) => {
      const value = detail && Object.prototype.hasOwnProperty.call(detail, "value")
        ? detail.value
        : getMarketingConsent();
      setVisible(value == null);
    };
    syncVisibility();
    return subscribeToMarketingConsent(syncVisibility);
  }, []);

  if (!MARKETING_ENABLED) return null;

  const choose = (value) => {
    setMarketingConsent(value);
    setVisible(false);
  };

  if (!visible) {
    return (
      <button
        type="button"
        onClick={resetMarketingConsent}
        className="fixed bottom-4 right-4 z-[70] inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/95 px-4 py-2 text-xs font-semibold text-slate-700 shadow-lg backdrop-blur transition hover:border-emerald-400 hover:text-emerald-700"
        aria-label="Abrir preferencias de cookies y privacidad"
      >
        <ShieldCheck className="h-4 w-4 text-emerald-600" />
        Cookies y privacidad
      </button>
    );
  }

  return (
    <aside
      role="dialog"
      aria-label="Preferencias de cookies y privacidad"
      className="fixed inset-x-4 bottom-4 z-[70] mx-auto max-w-3xl overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 text-white shadow-[0_24px_70px_rgba(15,23,42,0.32)]"
    >
      <div className="flex items-start gap-4 p-4 sm:p-5">
        <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-400/15 text-emerald-300 sm:flex">
          <BarChart3 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold tracking-tight">Cookies y privacidad</p>
              <p className="mt-1 text-xs leading-5 text-slate-300 sm:text-sm">
                Podemos usar estadisticas anonimas de navegacion para mejorar DTE Flow y sus guias. Nunca enviamos datos fiscales ni credenciales.
              </p>
            </div>
            <button
              type="button"
              onClick={() => choose("denied")}
              className="rounded-lg p-1 text-slate-400 transition hover:bg-white/10 hover:text-white"
              aria-label="Cerrar y rechazar medicion"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Link
              to="/privacy"
              className="inline-flex items-center gap-2 text-xs font-medium text-emerald-300 underline-offset-4 hover:underline"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Ver privacidad y preferencias
            </Link>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => choose("denied")}
                className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-200 transition hover:border-slate-500 hover:bg-white/5"
              >
                Ahora no
              </button>
              <button
                type="button"
                onClick={() => choose("granted")}
                className="rounded-xl bg-emerald-400 px-4 py-2 text-xs font-bold text-slate-950 transition hover:bg-emerald-300"
              >
                Aceptar y continuar
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
